import { AsYouType } from 'libphonenumber-js/min'
import type { CallingCode } from './types'

export interface PhoneNumber {
  /** A 1 to 3 digit number indicating the country or region where the phone number is assigned. */
  callingCode?: string
  /** A two-letter abbreviation for the phone number's country of origin, if it can be determined. */
  country?: string
  /** The entire phone number including the calling code. */
  internationalNumber: string
  /** Includes the national destination code (area code in the U.S.) combined with the subscriber number. */
  nationalNumber: string
  /** The phone number formatted as it would be dialed domestically, including the trunk prefix where applicable. */
  formattedNationalNumber: string
  /** The phone number in international format */
  formattedInternationalNumber?: string
  /** The phone number is considered possible if it has the correct number of digits for a given calling code. */
  isPossible: boolean
  /** A more strict alternative to isPossible. This is based on a large number of country-specific rules that are continuously changing. */
  isValid: boolean
}

export const parsePhoneNumber = (
  value: string,
  defaultCallingCode?: CallingCode
): PhoneNumber => {
  let asYouType: AsYouType
  try {
    asYouType = new AsYouType({ defaultCallingCode })
  } catch (error) {
    asYouType = new AsYouType()
  }

  asYouType.input(value)
  const phoneNumber = asYouType.getNumber()

  // phoneNumber will be undefined if no national (significant) number digits have been entered so far,
  // or if no defaultCallingCode has been provided and the value is a phone number without a "+" at the beginning.
  if (phoneNumber) {
    return {
      callingCode: phoneNumber.countryCallingCode as string,
      country: phoneNumber.country as string,
      internationalNumber: phoneNumber.number as string,
      nationalNumber: phoneNumber.nationalNumber as string,
      formattedNationalNumber: phoneNumber.formatNational(),
      formattedInternationalNumber: phoneNumber.formatInternational(),
      isPossible: phoneNumber.isPossible(),
      isValid: phoneNumber.isValid(),
    }
  }

  return {
    callingCode: defaultCallingCode,
    country: undefined,
    internationalNumber: defaultCallingCode ? `+${defaultCallingCode}` : '',
    nationalNumber: value,
    formattedNationalNumber: value,
    formattedInternationalNumber: defaultCallingCode
      ? `+${defaultCallingCode}`
      : '',
    isPossible: false,
    isValid: false,
  }
}

export const formatPhoneNumber = (
  nationalNumber: string,
  callingCode: string
) => {
  let asYouType: AsYouType
  try {
    asYouType = new AsYouType({ defaultCallingCode: callingCode })
  } catch (error) {
    asYouType = new AsYouType()
  }
  if (callingCode === '1') {
    const formatted = asYouType.input(nationalNumber)
    return removeLastParen(formatted)
  }
  const formatted = asYouType.input('+' + callingCode + nationalNumber)
  return formatted.split(' ').slice(1).join(' ')
}

/**
 * Remove the closing paren if it's the last character.
 * Otherwise the user cannot delete it: Every time you try to delete it the formatter adds it back!
 *
 * @param value
 */
const removeLastParen = (value: string) => {
  if (value[value.length - 1] === ')') {
    return value.slice(0, -1)
  }
  return value
}
